import * as React from 'react';
import { __ } from '@bart.sk-eshop/react-i18n';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router';
import styled, { css } from 'styled-components';
import { rem, rgba } from 'polished';
import { URL_SEARCH } from '../../constants';
import IconLazy from '../../components/_helpers/Icon/IconLazy';

export const audienceColor =
  (key: string) =>
  ({ theme }: { theme: any }) =>
    theme.color.audience[key];

/* ------------------------------- SEO ------------------------------ */

export const SITE_ORIGIN = 'https://www.daffer.sk';

const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Daffer spol. s r.o.',
  url: SITE_ORIGIN,
  logo: `${SITE_ORIGIN}/images/logo.svg`,
  email: 'daffer@daffer.sk',
  telephone: '+421 46 519 87 11',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Včelárska 1',
    postalCode: '971 01',
    addressLocality: 'Prievidza',
    addressCountry: 'SK',
  },
};

const WEBSITE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Daffer.sk',
  url: SITE_ORIGIN,
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_ORIGIN}${URL_SEARCH}?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

export interface StructuredDataProps {
  webSite?: boolean;
}

export const StructuredData = ({ webSite }: StructuredDataProps) => (
  <Helmet>
    <script type="application/ld+json">
      {JSON.stringify(ORGANIZATION_SCHEMA)}
    </script>
    {webSite ? (
      <script type="application/ld+json">
        {JSON.stringify(WEBSITE_SCHEMA)}
      </script>
    ) : null}
  </Helmet>
);

/* ------------------------------------------------------------------ *
 * Shared building blocks for the audience-intro landing pages
 * ("Pre školské zariadenia" / "Pre firmy a organizácie"). Both pages are
 * pixel rebuilds of the same Figma design system, so the layout scaffolding,
 * buttons and the bottom red footer banner are identical and live here.
 * Mobile-first; the desktop layout kicks in at `md` (992px).
 * ------------------------------------------------------------------ */

export const Page = styled.div`
  width: 100%;
`;

export const ContentBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(40)};
  padding: ${rem(16)} 0 ${rem(40)};
  /* The Figma mobile frame background is white (not the grey app background). */
  background: ${({ theme }) => theme.color.white};
  ${({ theme }) => theme.media('md')} {
    max-width: ${rem(1440)};
    margin: 0 auto;
    /* No horizontal padding here: inset sections add their own, full-bleed
       coloured sections span the whole box width. */
    padding: ${rem(40)} 0 ${rem(64)};
    gap: ${rem(48)};
  }
`;

export const Section = styled.section`
  width: 100%;
  padding: 0 ${rem(16)};
  ${({ theme }) => theme.media('md')} {
    padding: 0 ${rem(56)};
  }
`;

export const headingInset = css`
  ${({ theme }) => theme.media('md')} {
    margin-left: ${rem(32)};
    margin-right: ${rem(32)};
  }
`;

/* ----------------------------- Buttons ---------------------------- */

export const pillButtonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${rem(10)};
  padding: ${rem(8)} ${rem(24)};
  min-height: ${rem(44)};
  border-radius: ${rem(999)};
  font-size: ${rem(16)};
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  text-decoration: none;
  align-self: stretch;
  transition: background 0.15s ease;

  ${({ theme }) => theme.media('md')} {
    align-self: flex-start;
  }
`;

export const PillButton = styled(Link)`
  ${pillButtonBase};
  background: ${audienceColor('redCta')};
  color: ${({ theme }) => theme.color.white};

  &:hover {
    color: ${({ theme }) => theme.color.white};
    background: ${audienceColor('redCtaHover')};
    text-decoration: none;
  }
`;

export const OutlineButton = styled(Link)`
  ${pillButtonBase};
  background: transparent;
  border: ${rem(2)} solid ${audienceColor('redCta')};
  color: ${audienceColor('redCta')};

  &:hover {
    color: ${audienceColor('redCta')};
    background: ${({ theme }) => rgba(theme.color.audience.redCta, 0.06)};
    text-decoration: none;
  }
`;

export const YellowButton = styled(Link)`
  ${pillButtonBase};
  background: ${({ theme }) => theme.color.secondary};
  color: ${audienceColor('dark')};

  &:hover {
    color: ${audienceColor('dark')};
    background: ${audienceColor('yellowHover')};
    text-decoration: none;
  }
`;

export const desktopHeaderButton = css`
  display: none;
  ${({ theme }) => theme.media('md')} {
    display: inline-flex;
    flex: 0 0 auto;
  }
`;

/* ----------------------------- Headings --------------------------- */

export const sectionHeadingBase = css`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.headings};
  font-weight: 700;
  font-size: ${rem(28)};
  line-height: 1.36;
  ${({ theme }) => theme.media('md')} {
    font-size: ${rem(32)};
  }
`;

/* --------------------------- Badge icon --------------------------- *
 * A red rounded-square "badge" with a single icon-only SVG centred inside
 * (Figma feature badges / rep-feature icons). Previously each badge was one
 * flattened SVG that baked the red square in; now the square is real CSS so it
 * can be recoloured / resized freely and the asset carries only the glyph.
 * The caller sets the size (and any shadow) via `styled(BadgeIcon)`.
 * ------------------------------------------------------------------ */

const BadgeBox = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  background: ${audienceColor('red')};
  /* Matches the original rx 17 on a 60px square — proportional at any size. */
  border-radius: 28.3%;
`;

const BadgeGlyph = styled.img`
  display: block;
  width: 100%;
  height: 100%;
`;

export interface BadgeIconProps {
  src: string;
  alt?: string;
  className?: string;
}

export const BadgeIcon = ({ src, alt = '', className }: BadgeIconProps) => (
  <BadgeBox className={className}>
    <BadgeGlyph
      src={src}
      alt={alt}
      aria-hidden={alt ? undefined : true}
      loading="lazy"
      decoding="async"
    />
  </BadgeBox>
);

/* ------------------------------------------------------------------ *
 * Bottom red "billboard" footer (Figma "Group 4"): a red rounded-top
 * banner with a white card inside (logo, tagline, contacts). It sits ABOVE
 * the global site footer — these pages intentionally show two footers.
 * Identical on both audience-intro pages.
 * ------------------------------------------------------------------ */

const FooterBanner = styled.footer`
  width: 100%;
  background: ${audienceColor('redCta')};
  border-radius: ${rem(16)} ${rem(16)} 0 0;
  /* Padding = the red border framing the white card. */
  padding: ${rem(12)};
  ${({ theme }) => theme.media('md')} {
    max-width: ${rem(1700)};
    margin: 0 auto;
    border-radius: ${rem(32)} ${rem(32)} 0 0;
    padding: ${rem(40)};
  }
`;

const FooterCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(26)};
  background: ${({ theme }) => theme.color.white};
  border-radius: ${rem(12)};
  padding: ${rem(24)};
  ${({ theme }) => theme.media('md')} {
    border-radius: ${rem(24)};
    padding: ${rem(46)} ${rem(57)};
  }
`;

const FooterLogo = styled.img`
  width: ${rem(180)};
  height: auto;
  ${({ theme }) => theme.media('md')} {
    width: ${rem(212)};
  }
`;

const FooterTagline = styled.p`
  margin: 0;
  font-size: ${rem(24)};
  font-weight: 700;
  line-height: 1.33;
  color: ${audienceColor('dark')};
`;

const FooterContacts = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
`;

const FooterContact = styled.a`
  display: flex;
  align-items: center;
  gap: ${rem(16)};
  font-size: ${rem(15)};
  line-height: 1.6;
  color: ${audienceColor('dark')};
  text-decoration: none;

  &:hover {
    color: ${audienceColor('dark')};
    text-decoration: none;
  }
`;

const ContactBadge = styled.span`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(40)};
  height: ${rem(40)};
  border-radius: 50%;
  background: ${({ theme }) => rgba(theme.color.audience.red, 0.1)};
`;

const ContactIcon = styled(IconLazy)`
  width: ${rem(20)};
  height: ${rem(20)};
  fill: ${({ theme }) => theme.color.primary};
`;

export interface FooterBannerProps {
  phone?: string;
  mail?: string;
}

export const FooterBannerSection = ({ phone, mail }: FooterBannerProps) => {
  const footerPhone = phone || '+421 46 519 87 11';
  const footerMail = mail || 'daffer@daffer.sk';
  return (
    <FooterBanner>
      <FooterCard>
        <FooterLogo
          src="/images/logo.svg"
          alt="Daffer"
          loading="lazy"
          decoding="async"
        />
        <FooterTagline>
          {__('partner slovenských škôl')}
          <br />
          {__('od roku 1996')}
        </FooterTagline>
        <FooterContacts>
          <FooterContact href={`tel:${footerPhone.replace(/\s/g, '')}`}>
            <ContactBadge>
              <ContactIcon icon="phone" />
            </ContactBadge>
            {footerPhone}
          </FooterContact>
          <FooterContact href={`mailto:${footerMail}`}>
            <ContactBadge>
              <ContactIcon icon="mail" />
            </ContactBadge>
            {footerMail}
          </FooterContact>
          <FooterContact as="div">
            <ContactBadge>
              <ContactIcon icon="location" />
            </ContactBadge>
            <span>
              {__('Daffer spol. s r.o.')}
              <br />
              {__('Včelárska 1, 971 01 Prievidza')}
              <br />
              {__('Slovenská republika')}
            </span>
          </FooterContact>
        </FooterContacts>
      </FooterCard>
    </FooterBanner>
  );
};

export const mapFooterState = (state: any): FooterBannerProps => ({
  phone:
    state && state.general && state.general.contactInfo
      ? state.general.contactInfo.contactTel
      : undefined,
  mail:
    state && state.general && state.general.contactInfo
      ? state.general.contactInfo.contactEmail
      : undefined,
});

/* ------------------------------------------------------------------ *
 * Horizontal card carousel (Figma sliders): a scroll-snap track that
 * bleeds + fades to the right, with red round chevron arrows overlaid on
 * desktop and centred below on mobile. The track styling is shared; each
 * consumer styles its own cards (width via the card's flex-basis) and may
 * tweak the overlay-arrow vertical position via `styled(OverlayArrow)`.
 * ------------------------------------------------------------------ */

export const CarouselWrap = styled.div`
  position: relative;
`;

export const CarouselTrack = styled.div`
  display: flex;
  gap: ${rem(24)};
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  /* Keep cards aligned with the title: without this, snap eats the left padding. */
  scroll-padding-left: ${rem(16)};
  padding: 0 ${rem(16)};
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  ${({ theme }) => theme.media('md')} {
    scroll-padding-left: ${rem(56)};
    /* Inset on the left (aligned with the title), bleeding + fading on the right. */
    padding: 0 0 0 ${rem(56)};
    -webkit-mask-image: linear-gradient(
      to right,
      #000 calc(100% - ${rem(96)}),
      transparent 100%
    );
    mask-image: linear-gradient(
      to right,
      #000 calc(100% - ${rem(96)}),
      transparent 100%
    );
  }
`;

export const MobileArrows = styled.div`
  display: flex;
  justify-content: center;
  gap: ${rem(16)};
  padding: 0 ${rem(16)};
  ${({ theme }) => theme.media('md')} {
    display: none;
  }
`;

const arrowButtonStyles = css`
  align-items: center;
  justify-content: center;
  width: ${rem(48)};
  height: ${rem(48)};
  border: none;
  border-radius: 50%;
  background: ${({ theme }) => theme.color.primary};
  box-shadow: 0 0 0 ${rem(3)} rgba(0, 0, 0, 0.1);
  color: ${({ theme }) => theme.color.white};
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${audienceColor('redArrowHover')};
  }

  svg {
    width: ${rem(32)};
    height: ${rem(32)};
  }
  svg path {
    stroke-width: 1.2;
  }
`;

export const ArrowButton = styled.button`
  display: flex;
  flex: 0 0 auto;
  ${arrowButtonStyles};
`;

export const OverlayArrow = styled.button`
  display: none;
  ${({ theme }) => theme.media('md')} {
    display: flex;
    position: absolute;
    top: ${rem(160)};
    transform: translateY(-50%);
    z-index: 2;
    ${arrowButtonStyles};
    &.left {
      left: ${rem(16)};
    }
    &.right {
      right: ${rem(16)};
    }
    &.hidden {
      display: none;
    }
  }
`;

export const Chevron = ({ dir }: { dir: 'left' | 'right' }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d={dir === 'left' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'}
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const useCarousel = (step = 304) => {
  const carouselRef = React.useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = React.useState(false);
  const [canRight, setCanRight] = React.useState(false);

  const updateArrows = React.useCallback(() => {
    const el = carouselRef.current;
    if (!el) {
      return;
    }
    setCanLeft(el.scrollLeft > 8);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  }, []);

  const scroll = (dir: number) => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: dir * step, behavior: 'smooth' });
    }
  };

  return { carouselRef, canLeft, canRight, updateArrows, scroll };
};

/* ------------------------------------------------------------------ *
 * Promo banners (Figma "Doprava zdarma" / "Sieť papiernictiev"): two
 * overlapping rounded panels — a maroon panel on the left (image bleeds out
 * of it) and a red panel on the right that overlaps it, on top. Identical on
 * the firmy and "pre seba" pages; images are reused from /images/firmy-intro.
 * ------------------------------------------------------------------ */

const Promo = styled(Section)`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  /* On desktop the promo banners can be inset much more than the rest of the
     content (Figma, "firmy"). Padding (not margin) keeps the full-width section
     from overflowing the page given its box-sizing: border-box + width: 100%.
     The "pre seba" page uses the classic section gutter instead ($wide=false). */
  ${({ theme }) => theme.media('md')} {
    flex-direction: row;
    gap: ${rem(24)};
    padding-left: ${({ $wide }) => rem($wide ? 120 : 56)};
    padding-right: ${({ $wide }) => rem($wide ? 120 : 56)};
    & > * {
      flex: 1 1 0;
    }
  }
`;

const PromoBanner = styled(Link)`
  position: relative;
  /* Own stacking context so the image (z-index 3) can sit above the red panel
     while staying contained within the banner. */
  z-index: 0;
  display: flex;
  align-items: stretch;
  text-decoration: none;
  color: inherit;
  &:hover,
  &:focus {
    color: inherit;
    text-decoration: none;
  }
`;

const PromoMaroon = styled.div`
  position: relative;
  /* No z-index here: it must not trap the image's stacking (so the image can
     paint above the red panel). */
  flex: 0 0 ${rem(120)};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  background: ${audienceColor('maroon')};
  /* Only the left corners are rounded — the right side tucks under the red. */
  border-radius: ${rem(16)} 0 0 ${rem(16)};
  ${({ theme }) => theme.media('md')} {
    flex-basis: ${rem(160)};
    border-radius: ${rem(24)} 0 0 ${rem(24)};
  }
`;

const PromoImg = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  /* Above the red panel so it can stick out over it (Figma). */
  z-index: 3;
  transform: translate(-50%, -50%);
  width: ${rem(150)};
  height: ${rem(80)};
  object-fit: contain;
  ${({ theme }) => theme.media('md')} {
    width: ${rem(240)};
    height: ${rem(120)};
  }
`;

const PromoImgMap = styled(PromoImg)`
  transform: translate(calc(-63% + ${rem(8)}), calc(-40% - ${rem(14)}));
  ${({ theme }) => theme.media('md')} {
    transform: translate(calc(-55% + ${rem(12)}), calc(-50% - ${rem(18)}));
  }
`;

const PromoRed = styled.div`
  position: relative;
  z-index: 1;
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${rem(8)};
  /* Overlap onto the maroon panel so the red rounded corner sits over it. */
  margin-left: -${rem(16)};
  background: ${audienceColor('red')};
  border-radius: ${rem(16)};
  padding: ${rem(20)} ${rem(24)};
  color: ${({ theme }) => theme.color.white};
  ${({ theme }) => theme.media('md')} {
    margin-left: -${rem(24)};
    border-radius: ${rem(24)};
    padding: ${rem(28)} ${rem(32)};
  }
`;

const PromoTitle = styled.span`
  font-family: ${({ theme }) => theme.fonts.headings};
  font-size: ${rem(21)};
  font-weight: 700;
  line-height: 1.3;
`;

const PromoDesc = styled.span`
  font-size: ${rem(15)};
  font-weight: 400;
  line-height: 1.4;
  max-width: ${rem(350)};
`;

export interface PromoBannerData {
  img: string;
  title: string;
  desc: string;
  up?: boolean;
  link: string;
}

export const PROMO_BANNERS: PromoBannerData[] = [
  {
    img: 'promo-van.webp',
    title: __('Doprava zdarma'),
    desc: __('Doprava po celej SR pri nákupe nad 49 € zadarmo.'),
    link: '/doprava',
  },
  {
    img: 'promo-map.webp',
    title: __('Sieť papiernictiev'),
    desc: __('Sme tu pre Vás v 20 predajniach po celom Slovensku'),
    up: true,
    link: '/predajne',
  },
];

export const SELF_PROMO_BANNERS: PromoBannerData[] = PROMO_BANNERS.map((b) =>
  b.link === '/doprava' ? { ...b, link: '/casto-kladene-otazky' } : b,
);

export interface PromoBannersProps {
  asset?: string;
  banners?: PromoBannerData[];
  wideInset?: boolean;
}

export const PromoBanners = ({
  asset = '/images/firmy-intro',
  banners = PROMO_BANNERS,
  wideInset = true,
}: PromoBannersProps) => (
  <Promo $wide={wideInset}>
    {banners.map((b) => {
      const Img = b.up ? PromoImgMap : PromoImg;
      return (
        <PromoBanner key={b.title} to={b.link}>
          <PromoMaroon>
            <Img
              src={`${asset}/${b.img}`}
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
            />
          </PromoMaroon>
          <PromoRed>
            <PromoTitle>{b.title}</PromoTitle>
            <PromoDesc>{b.desc}</PromoDesc>
          </PromoRed>
        </PromoBanner>
      );
    })}
  </Promo>
);

/* ------------------------------------------------------------------ *
 * "Folder" product/category card (Figma): a yellow folder back with the
 * photo overflowing its top, then a white body (title + list + CTA) that
 * overlaps the folder. Shared by the firmy "Ponuka produktov a služieb"
 * cards (icon + text rows) and the "pre seba" category cards (subcategory
 * links). The list-item primitives below cover both variants.
 * ------------------------------------------------------------------ */

export const ProductCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(64)};
  margin-top: ${rem(24)};
  ${({ theme }) => theme.media('md')} {
    margin-top: ${rem(0)};
    flex-direction: row;
    align-items: stretch;
    & > * {
      flex: 1 1 0;
    }
  }
`;

export const ProductCard = styled.div`
  display: flex;
  flex-direction: column;
`;

export const ProductFolder = styled.div`
  background: ${audienceColor('yellow')};
  border-radius: ${rem(16)} ${rem(16)} 0 0;
  height: ${rem(120)};
  display: flex;
  align-items: flex-end;
  justify-content: center;
  overflow: visible;
`;

export const ProductImg = styled.img`
  display: block;
  width: auto;
  max-width: 86%;
  max-height: ${rem(180)};
  height: auto;
  object-fit: contain;
  margin-bottom: ${rem(-32)};
`;

export const ProductBody = styled.div`
  position: relative;
  z-index: 1;
  margin-top: -${rem(24)};
  background: ${({ theme }) => theme.color.white};
  border-radius: ${rem(16)};
  box-shadow: 0 ${rem(4)} ${rem(16)} rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: ${rem(24)};
  padding: ${rem(24)};
  flex: 1 1 auto;
  ${({ theme }) => theme.media('md')} {
    padding: ${rem(28)};
  }
`;

export const ProductTitle = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.headings};
  font-size: ${rem(24)};
  font-weight: 700;
  line-height: 1.33;
  color: ${audienceColor('dark')};
`;

export const ProductList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
`;

export const ProductItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${rem(16)};
`;

export const ProductItemIcon = styled.img`
  width: ${rem(24)};
  height: ${rem(24)};
  flex: 0 0 auto;
`;

export const ProductItemText = styled.span`
  font-size: ${rem(16)};
  font-weight: 400;
  line-height: 1.4;
  color: ${audienceColor('dark')};
  text-decoration: underline;
`;

export const ProductItemLink = styled(Link)`
  font-size: ${rem(16)};
  font-weight: 400;
  line-height: 1.4;
  color: ${audienceColor('dark')};
  text-decoration: underline;

  &:hover {
    color: ${audienceColor('dark')};
    text-decoration: none;
  }
`;

export const ProductButtonRow = styled.div`
  display: flex;
  /* Column so the button stretches full-width on mobile (Figma). */
  flex-direction: column;
  margin-top: auto;
`;
